import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Calendar, Key, ChevronRight, Store, HelpCircle } from "lucide-react";
import { useAuth, API } from "../../context/AuthContext";
import { ChangePassword } from "../../components/shared/ChangePassword";
import AccountantMainDashboard from "./AccountantMainDashboard";
import EventDetailDashboard from "./EventDetailDashboard";

const AccountantMerchants = () => {
  const [merchants, setMerchants] = useState([]);
  const { authAxios } = useAuth();

  useEffect(() => { fetchMerchants(); }, []);
  const fetchMerchants = async () => { const res = await authAxios.get("/merchants"); setMerchants(res.data); };

  const handleToggle = async (id, isActive) => {
    try { await authAxios.put(`/merchants/${id}`, { is_active: !isActive }); toast.success(`Merchant ${isActive ? "deactivated" : "activated"}`); fetchMerchants(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const handleResetPassword = async (id) => {
    try { await authAxios.post(`/merchants/${id}/reset-password`); toast.success("Password reset email sent"); fetchMerchants(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const handleLogout = async (id) => {
    try { await authAxios.post(`/merchants/${id}/logout`); toast.success("Merchant logged out"); fetchMerchants(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="accountant-merchants">
      <h3 className="font-bold text-[#1e3a5f] mb-4">Merchants</h3>
      <div className="space-y-3">
        {merchants.map(m => (
          <div key={m.id} className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><Store className="w-6 h-6 text-green-500" /></div>
                <div><p className="font-bold text-slate-800">{m.name}</p><p className="text-slate-500 text-sm">{m.email}</p>{m.is_logged_in && <span className="text-xs text-green-500">Online</span>}</div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${m.is_active ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-400"}`}>{m.is_active ? "Active" : "Inactive"}</span>
            </div>
            <div className="flex gap-3 mt-3">
              <button onClick={() => handleResetPassword(m.id)} className="flex items-center gap-1 text-amber-500 text-sm"><Key className="w-4 h-4" /> Reset Password</button>
              {m.is_logged_in && <button onClick={() => handleLogout(m.id)} className="flex items-center gap-1 text-orange-500 text-sm"><LogOut className="w-4 h-4" /> Logout</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AccountantDashboard() {
  const [tab, setTab] = useState("events");
  const [selectedEventId, setSelectedEventId] = useState(null);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (!user || user.role !== "accountant") navigate("/" + (window.location.pathname.split("/")[1] || "mymaitree") + "/admin"); }, [user]);

  const handleLogout = async () => {
    // iter139: land on the tenant admin login, not the root splash.
    const slug = window.location.pathname.split("/")[1] || "mymaitree";
    try { await axios.post(`${API}/accountant/logout`, {}, { withCredentials: true }); } catch (e) {}
    logout(); navigate(`/${slug}/admin`);
  };

  const activeTab = selectedEventId ? "events" : tab;

  return (
    <div className="min-h-screen" data-testid="accountant-dashboard">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src="/mymaitree-logo.png" alt="My Maitree" className="h-8 w-auto" onError={(e) => { e.target.style.display = 'none'; }} /><div><p className="text-slate-400 text-xs">Accountant</p><h1 className="text-sm font-bold text-[#1e3a5f]">{user?.name || "Accountant"}</h1></div></div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setTab("settings"); setSelectedEventId(null); }} className={`p-2 rounded-full hover:bg-slate-100 ${tab === "settings" ? "text-[#1e3a5f]" : "text-slate-400"}`} data-testid="header-settings-btn"><Key className="w-5 h-5" /></button>
            <button onClick={() => navigate("/help?role=accountant")} className="p-2 rounded-full hover:bg-slate-100 text-slate-400" data-testid="help-btn"><HelpCircle className="w-5 h-5" /></button>
            <button onClick={handleLogout} className="p-2 rounded-full hover:bg-red-50 text-red-500" data-testid="logout-btn"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        {tab !== "settings" && !selectedEventId && (
          <div className="max-w-lg mx-auto flex border-t">
            <button onClick={() => setTab("events")} className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium border-b-2 ${activeTab === "events" ? "border-[#1e3a5f] text-[#1e3a5f]" : "border-transparent text-slate-400"}`} data-testid="nav-events"><Calendar className="w-4 h-4" /> Events</button>
            <button onClick={() => setTab("config")} className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium border-b-2 ${activeTab === "config" ? "border-[#1e3a5f] text-[#1e3a5f]" : "border-transparent text-slate-400"}`} data-testid="nav-config"><Store className="w-4 h-4" /> Merchants</button>
          </div>
        )}
      </div>
      <div className="max-w-lg mx-auto p-6">
        {tab === "events" && !selectedEventId && <AccountantMainDashboard setSelectedEventId={setSelectedEventId} />}
        {selectedEventId && <EventDetailDashboard eventId={selectedEventId} onBack={() => setSelectedEventId(null)} />}
        {tab === "config" && !selectedEventId && (<div data-testid="accountant-config"><AccountantMerchants /></div>)}
        {tab === "settings" && <ChangePassword onBack={() => setTab("events")} role="accountant" />}
      </div>
    </div>
  );
}
